'use client';

/**
 * SpacesView - Portal view for the Room Designer
 *
 * Fetches the user's wishlisted properties, extracts their images,
 * and passes them into the SpacesWizard in portal mode.
 *
 * The wizard is never unmounted once initially shown — transient loading
 * states (e.g. Supabase token refresh triggering useWishlist reload) are
 * absorbed so the user doesn't lose in-progress design wizard state.
 */

import { useWishlist } from '@/hooks/useWishlist';
import { useSpacesUsage } from '@/hooks/useSpacesUsage';
import type { Property } from '@/lib/api/clients/properties';
import type { MediaLink } from '@/lib/api/clients/properties';
import type { WishlistPropertyImage, PropertyImage } from '@/components/spaces/types';
import { SpacesWizard } from '@/components/spaces';
import { useTenant } from '@/contexts/TenantContext';
import { Heart, Loader2, Sparkles, Wand2 } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import React, { useCallback, useEffect, useRef, useState } from 'react';

function buildLocationString(location?: Property['location'] | null): string {
  if (!location) return 'Location not available';

  const parts: string[] = [];
  if (location.neighbourhood) parts.push(location.neighbourhood);
  else if (location.suburb) parts.push(location.suburb);
  if (location.city) parts.push(location.city);
  if (location.region) parts.push(location.region);
  if (location.country) parts.push(location.country);

  return parts.length > 0 ? parts.join(', ') : 'Location not available';
}

function extractPropertyImages(property: Property): PropertyImage[] {
  const images: PropertyImage[] = [];
  const seen = new Set<string>();

  const addImage = (
    id: string,
    url: string,
    thumbnailUrl?: string | null,
    alt?: string,
    usageContext?: string
  ) => {
    if (seen.has(url) || !url) return;
    seen.add(url);
    const entry: PropertyImage = { id, url };
    if (thumbnailUrl) entry.thumbnailUrl = thumbnailUrl;
    if (alt) entry.alt = alt;
    if (usageContext) entry.usageContext = usageContext;
    images.push(entry);
  };

  if (property.heroMedia) {
    addImage(
      property.heroMedia.id,
      property.heroMedia.url,
      property.heroMedia.thumbnailUrl,
      property.heroMedia.title || property.name,
      'PRIMARY_HERO'
    );
  }

  if (property.primaryImageUrl && !seen.has(property.primaryImageUrl)) {
    addImage(
      `primary-${property.id}`,
      property.primaryImageUrl,
      undefined,
      property.name,
      'PRIMARY_HERO'
    );
  }

  if (property.mediaLinks) {
    const imageLinks = property.mediaLinks
      .filter(
        (ml: MediaLink) =>
          ml.media?.url &&
          (ml.media.mimeType?.startsWith('image/') ||
            ml.media.mediaType === 'IMAGE' ||
            ml.usageContext === 'GALLERY' ||
            ml.usageContext === 'PRIMARY_HERO')
      )
      .sort((a: MediaLink, b: MediaLink) => a.sortOrder - b.sortOrder);

    for (const ml of imageLinks) {
      addImage(
        ml.media.id,
        ml.media.url,
        ml.media.thumbnailUrl,
        ml.media.title || ml.contextualTitle || property.name,
        ml.usageContext
      );
    }
  }

  return images;
}

export const SpacesView: React.FC = () => {
  const { wishlists, isLoading: isLoadingWishlists, getWishlist } = useWishlist();
  const { organisationId } = useTenant();
  const usage = useSpacesUsage();
  const pathname = usePathname();

  const [wishlistProperties, setWishlistProperties] = useState<WishlistPropertyImage[]>([]);
  const [isLoadingProperties, setIsLoadingProperties] = useState(false);
  const [hasFetchedProperties, setHasFetchedProperties] = useState(false);
  const hasEverLoadedRef = useRef(false);
  const fetchedForRef = useRef<string>('');

  const getLocale = (): string => {
    const match = pathname.match(/^\/([a-z]{2})(\/|$)/);
    return match?.[1] ?? 'en';
  };
  const locale = getLocale();

  const fetchWishlistProperties = useCallback(async () => {
    if (wishlists.length === 0) {
      setWishlistProperties([]);
      setHasFetchedProperties(true);
      return;
    }

    const wishlistKey = wishlists.map((w) => `${w.id}:${w.itemCount}`).join(',');
    if (fetchedForRef.current === wishlistKey) return;
    fetchedForRef.current = wishlistKey;

    setIsLoadingProperties(true);

    try {
      const wishlistPromises = wishlists.map(async (wishlist) => {
        if (wishlist.itemCount === 0) return [];
        try {
          const wishlistWithItems = await getWishlist(wishlist.id);
          if (!wishlistWithItems?.items) return [];
          return wishlistWithItems.items;
        } catch {
          return [];
        }
      });

      const results = await Promise.all(wishlistPromises);
      const allItems = results.flat();

      const propertyItems = allItems.filter((item) => item.entityType === 'PROPERTY');
      const propertyIds = [...new Set(propertyItems.map((item) => item.entityId))];

      if (propertyIds.length === 0) {
        setWishlistProperties([]);
        setHasFetchedProperties(true);
        return;
      }

      const resp = await fetch('/api/properties', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organisationId,
          propertyIds,
          includeRelations: true,
        }),
      });

      if (!resp.ok) {
        setWishlistProperties([]);
        return;
      }

      const { properties } = (await resp.json()) as {
        properties: Record<string, Property>;
        errors: Record<string, string>;
      };

      const mapped: WishlistPropertyImage[] = [];
      for (const [id, property] of Object.entries(properties)) {
        const images = extractPropertyImages(property);
        mapped.push({
          propertyId: id,
          propertyName: property.name || 'Untitled Property',
          propertyLocation: buildLocationString(property.location),
          images,
        });
      }

      setWishlistProperties(mapped);
      hasEverLoadedRef.current = true;
    } catch (err) {
      console.error('[SpacesView] Failed to fetch wishlist properties:', err);
      setWishlistProperties([]);
    } finally {
      setIsLoadingProperties(false);
      setHasFetchedProperties(true);
    }
  }, [wishlists, getWishlist, organisationId]);

  useEffect(() => {
    if (!isLoadingWishlists && wishlists.length > 0) {
      fetchWishlistProperties();
    } else if (wishlists.length === 0 && !isLoadingWishlists) {
      setWishlistProperties([]);
      setHasFetchedProperties(true);
    }
  }, [wishlists, isLoadingWishlists, fetchWishlistProperties]);

  // Only show the loading spinner on the very first load.
  // Once we've successfully loaded properties, never show the loading state
  // again — this prevents the wizard from being unmounted when Supabase
  // token refresh or tab visibility change triggers a wishlist re-fetch.
  const isInitialLoading = !hasEverLoadedRef.current &&
    (isLoadingWishlists || isLoadingProperties || !hasFetchedProperties);

  if (isInitialLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-stone-400" />
        <p className="mt-4 text-sm text-stone-400">Loading your saved properties...</p>
      </div>
    );
  }

  if (!hasEverLoadedRef.current && (wishlists.length === 0 || wishlistProperties.length === 0)) {
    const hasWishlists = wishlists.length > 0;
    return (
      <div className="space-y-8 animate-[fadeIn_0.3s_ease-out] pb-12">
        <div>
          <h2 className="text-3xl font-semibold text-stone-900 tracking-tight">Room Designer</h2>
          <p className="text-stone-500 mt-2 text-lg font-light">
            AI-powered interior design for your saved properties.
          </p>
        </div>

        <div className="text-center py-20 bg-stone-50 rounded-3xl border border-dashed border-stone-200">
          <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
            <Heart size={24} className="text-stone-300" />
          </div>
          <h3 className="text-lg font-medium text-stone-900">
            {hasWishlists ? 'No properties found' : 'No saved properties'}
          </h3>
          <p className="text-stone-500 text-sm mt-1 max-w-md mx-auto">
            {hasWishlists
              ? 'Your wishlists don\u2019t contain any properties yet. Browse and save some properties to get started.'
              : 'Save properties to your wishlist first, then come back here to reimagine their rooms with AI-powered design.'}
          </p>
          <Link
            href={`/${locale}/properties`}
            className="inline-block mt-6 px-6 py-2.5 rounded-xl bg-stone-900 text-white text-sm font-medium hover:bg-stone-800 transition-colors"
          >
            Browse Properties
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-[fadeIn_0.3s_ease-out] pb-12">
      <div className="flex justify-between items-end">
        <div>
          <h2 className="text-3xl font-semibold text-stone-900 tracking-tight flex items-center gap-3">
            <Wand2 size={28} className="text-stone-400" />
            Room Designer
          </h2>
          <p className="text-stone-500 mt-2 text-lg font-light">
            Reimagine your saved properties with AI-powered interior design.
          </p>
        </div>
        <div className="hidden md:flex items-center gap-2 bg-stone-100 px-4 py-2 rounded-xl">
          <Sparkles size={14} className="text-stone-500" />
          <span className="text-sm font-medium text-stone-600">
            {usage.remaining} of {usage.maxAllowed} designs remaining
          </span>
        </div>
      </div>

      <div className="rounded-3xl overflow-hidden border border-stone-200 shadow-sm bg-stone-950">
        <SpacesWizard
          mode="portal"
          wishlistProperties={wishlistProperties}
          usageCount={usage.count}
          maxUsage={usage.maxAllowed}
          onUsageIncrement={usage.incrementUsage}
          onUsageSync={usage.syncFromServer}
        />
      </div>
    </div>
  );
};
